import { Block } from '../classes/Block';
import { ApiBlock, ApiCardGroup } from './apiTypes';
import { CardGroup } from './cardGroups';
import { getBlockTimingValues, CardTimingValues } from './cardTiming';
import { getLabelValueExchangedRowValues } from './labelValueExchange';

export interface RenderPayloadColumnDef {
  field: string;
}

export type ApiPropertiesMode = 'render' | 'preview';

export interface TransformBlockToApiOptions {
  columnDefs: RenderPayloadColumnDef[];
  propertiesMode?: ApiPropertiesMode;
  overrideColors?: boolean;
  includeId?: boolean;
}

export interface BuildCardsApiPayloadOptions {
  blocks: Block[];
  cardGroups: CardGroup[];
  columnDefs: RenderPayloadColumnDef[];
  propertiesMode?: ApiPropertiesMode;
  overrideColors?: boolean;
  includeId?: boolean;
}

function normalizeLogoValue(value: string): string {
  try {
    JSON.parse(value);
    return value;
  } catch {
    const scaleValue: number = parseFloat(value);
    return JSON.stringify({ scale: Number.isNaN(scaleValue) ? 100 : scaleValue });
  }
}

function buildApiRows(block: Block, columnDefs: RenderPayloadColumnDef[]): string[][] {
  const rows: string[][] = [];
  if (!block.dataRows || block.dataRows.length === 0) {
    return rows;
  }

  for (const dataRow of block.dataRows) {
    const rowValues: string[] = [];
    for (const colDef of columnDefs) {
      let value: string = String(dataRow[colDef.field] || '');
      if (value && block.isLogoBlock && colDef.field === 'col2') {
        value = normalizeLogoValue(value);
      }
      rowValues.push(value);
    }

    rows.push(getLabelValueExchangedRowValues(rowValues, block.exchangeLabelValue));
  }

  return rows;
}

export function transformBlockToApiFormat(
  block: Block,
  options: TransformBlockToApiOptions
): ApiBlock {
  const propertiesMode: ApiPropertiesMode = options.propertiesMode ?? 'render';
  const apiBlock: ApiBlock = {
    name: block.name,
    properties: propertiesMode === 'preview'
      ? block.toPreviewApiProperties()
      : block.toRenderApiProperties(options.overrideColors ?? false),
    rows: buildApiRows(block, options.columnDefs),
  };
  const cardTiming: CardTimingValues = getBlockTimingValues(block);
  apiBlock.properties.card_fade_in_frames = cardTiming.cardFadeInFrames;
  apiBlock.properties.card_duration_fully_up_frames = cardTiming.cardDurationFullyUpFrames;
  apiBlock.properties.card_fade_out_frames = cardTiming.cardFadeOutFrames;
  apiBlock.properties.card_slug_frames = cardTiming.cardSlugFrames;

  if (options.includeId) {
    apiBlock.id = block.id;
  }

  return apiBlock;
}

export function buildCardsApiPayload(options: BuildCardsApiPayloadOptions): ApiCardGroup[] {
  const visibleBlocks: Block[] = options.blocks.filter((block: Block) => !block.isHidden);
  const blockById: Map<string, Block> = new Map<string, Block>(
    visibleBlocks.map((block: Block) => [block.id, block])
  );
  const assignedIds: Set<string> = new Set<string>(
    options.cardGroups.flatMap((group: CardGroup) => group.blockIds)
  );
  const unassignedBlocks: Block[] = visibleBlocks.filter((block: Block) => !assignedIds.has(block.id));

  const groupedCards: ApiCardGroup[] = options.cardGroups
    .map((group: CardGroup): ApiCardGroup => ({
      card: group.blockIds
        .map((blockId: string) => blockById.get(blockId))
        .filter((block: Block | undefined): block is Block => block !== undefined)
        .map((block: Block) => transformBlockToApiFormat(block, options)),
    }))
    .filter((group: ApiCardGroup) => group.card.length > 0);

  if (unassignedBlocks.length > 0) {
    groupedCards.push({
      card: unassignedBlocks.map((block: Block) => transformBlockToApiFormat(block, options)),
    });
  }

  return groupedCards;
}
